import { VideoThumbnailSelector } from "components";
import { MediaDirectoryKeys } from "constants/enums";
import {
  generateVideoThumbnails,
  VideoThumbnail,
  ThumbnailSize
} from "helpers/generateVideoThumbnails";
import { useDirectUploadToS3 } from "libs/directUpload";
import { ProductsFieldForForms } from "modules/products";
import { useState, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Checkbox } from "ui";

import { FeedInsertFormValues } from "../FeedInsertForm.types";

import styles from "../FeedInsertForm.module.scss";

interface VideoMediaFieldProps {
  index: number;
  videoFile: File | null;
  thumbnailSize: ThumbnailSize;
}

const VideoMediaField = ({
  index,
  videoFile,
  thumbnailSize
}: VideoMediaFieldProps) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<FeedInsertFormValues>();
  const { uploader } = useDirectUploadToS3();

  const [thumbnails, setThumbnails] = useState<VideoThumbnail[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (!videoFile) {
      setThumbnails([]);
      setSelectedIndex(0);
      return;
    }

    const generate = async () => {
      setIsGenerating(true);
      try {
        const generated = await generateVideoThumbnails(
          videoFile,
          thumbnailSize
        );
        setThumbnails(generated);
        setSelectedIndex(0);

        if (generated.length > 0) {
          await uploadThumbnail(generated[0]);
        }
      } catch {
        toast.error(t("Failed to generate thumbnails"));
      } finally {
        setIsGenerating(false);
      }
    };

    generate();
  }, [videoFile, thumbnailSize.width, thumbnailSize.height]);

  const uploadThumbnail = async (thumbnail: VideoThumbnail) => {
    setIsUploading(true);
    try {
      const file = new File([thumbnail.blob], "thumbnail.jpg", {
        type: "image/jpeg"
      });

      const fileWithMetadata = Object.assign(file, {
        width: thumbnailSize.width,
        height: thumbnailSize.height
      });

      const media = await uploader(fileWithMetadata, {
        directoryKey: MediaDirectoryKeys.ProductFeedInsert,
        onError: () => {
          toast.error(t("Failed to upload thumbnail"));
        }
      });

      if (media) {
        setValue(`media.${index}.thumbnail`, media);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleSelectThumbnail = async (newIndex: number) => {
    if (newIndex === selectedIndex || !thumbnails[newIndex]) return;

    setSelectedIndex(newIndex);
    await uploadThumbnail(thumbnails[newIndex]);
  };

  return (
    <div className={styles.form_mediaFieldContent}>
      <Controller
        control={control}
        name={`media.${index}.productVariants`}
        render={({
          field: { value: productsList, onChange: onChangeProductsList }
        }) => (
          <Controller
            control={control}
            name={`media.${index}.productSet`}
            render={({
              field: { value: productSet, onChange: onChangeProductSet }
            }) => (
              <ProductsFieldForForms
                productsList={productsList ?? []}
                onChangeProductsList={onChangeProductsList}
                productSet={productSet}
                onChangeProductSet={onChangeProductSet}
              />
            )}
          />
        )}
      />

      <Controller
        control={control}
        name={`media.${index}.allowDisplayInFullScreenMode`}
        render={({ field: { value, onChange } }) => (
          <Checkbox
            checked={value ?? true}
            onChange={(e) => onChange(e.target.checked)}
            label={t("Allow display in full screen mode")}
          />
        )}
      />

      <VideoThumbnailSelector
        thumbnails={thumbnails}
        selectedIndex={selectedIndex}
        onSelect={handleSelectThumbnail}
        isLoading={isGenerating || isUploading}
        aspectRatio={thumbnailSize.width / thumbnailSize.height}
      />
    </div>
  );
};

export default VideoMediaField;
